import Cookies from 'js-cookie';
import { flagEnabled } from './featureFlags';
import { RETURNS_ORDER_STATUS, SWAPS_NEW } from './routes';
import { resolveOutageNba } from './outage';

export const MAX_NBAS = 3;
// NBA ids (also used as dismissal keys, mirroring legacy NbaFactory ids)
export const NBA_RETURN_STATUS = 'returnStatus';
export const NBA_DISMISSED_COOKIE = 'nba_dismissed';
export const NBA_ELIGIBLE_DEVICE_SWAP = 'eligibleDeviceSwap';
const HOUR_IN_MS = 3600 * 1000;

export const inTheLastNHours = (date, hours) => {
  const time = new Date(date).getTime();
  if (Number.isNaN(time)) return false;
  return Date.now() - time <= hours * HOUR_IN_MS;
};

// --- Dismissal (cookie-backed, parity with legacy NbaService) ---
export const getDismissedNbas = () => (
  (Cookies.get(NBA_DISMISSED_COOKIE) || '').split(',').filter(Boolean)
);

export const isNbaDismissed = (id, dismissed = getDismissedNbas()) => (
  dismissed.indexOf(id) > -1
);

const getSubscribedLobs = (accountData = {}) => {
  const services = accountData && typeof accountData === 'object'
    ? accountData.services
    : null;

  if (!services || typeof services !== 'object') return [];

  return Object.keys(services)
    .filter(lob => services[lob] && services[lob].subscribed);
};


export const dismissNbaCookie = (id) => {
  const dismissed = getDismissedNbas();
  if (dismissed.indexOf(id) === -1) {
    dismissed.push(id);
    Cookies.set(NBA_DISMISSED_COOKIE, dismissed.join(','), {
      domain: window.location.hostname,
    });
  }
  return dismissed;
};

export const canViewSwaps = (accountPerms, accountData) => {
  const hasSwapPerms = accountPerms.some(role => role === 'PRIMARY' || role === 'BILLPAY');
  return hasSwapPerms && accountData.status === 'ACTIVE' && !accountData.prepaid;
};


export const buildReturnStatusNba = (returnsList = []) => {
  let returns = returnsList.filter(item => item.returnInitiation);
  // Remove non-digital returns when the NBA-specific flag is on
  if (flagEnabled('disableNonDigitalReturnsNba')) {
    returns = returns.filter(item => item.returnInitiation.returnSource !== 'UPSStore');
  }

  if (!returns.length) return null;

  // Most recent return by initiation date
  const mostRecentReturn = returns.reduce((returnDevicesAccumulator, returningDevice) => {
    const returnDeviceAccDate = returnDevicesAccumulator.returnInitiation
      && new Date(returnDevicesAccumulator.returnInitiation.returnInitiationDate).getTime();
    const deviceDate = new Date(returningDevice.returnInitiation.returnInitiationDate).getTime();
    const hasNewerValidDate = Number.isNaN(returnDeviceAccDate)
      ? !Number.isNaN(deviceDate)
      : deviceDate > returnDeviceAccDate;
    if (!returnDevicesAccumulator.returnInitiation || hasNewerValidDate) {
      return {
        ...returningDevice,
        returnOrderStatus: returningDevice.returnOrderStatus?.toUpperCase(),
      };
    }
    return returnDevicesAccumulator;
  }, {});

  const { returnOrderStatus: status, returnInitiation } = mostRecentReturn;

  // Only show for returns initiated in the last 30 days
  const THIRTY_DAYS_IN_HOURS = 30 * 24;
  if (!inTheLastNHours(returnInitiation.returnInitiationDate, THIRTY_DAYS_IN_HOURS)) {
    return null;
  }

  let nbaPresentation;

  if (
    status === 'INITIATED'
    || (status === 'IN-PROGRESS' && returnInitiation.returnSource === 'UPSStore')
  ) {
    nbaPresentation = {
      svgIcon: 'check',
      name: 'Your Return is Underway',
      description: 'Track your return or reprint your shipping label anytime.',
    };
  } else if (status === 'IN-PROGRESS') {
    nbaPresentation = {
      svgIcon: 'box-closed',
      name: 'Package Scanned',
      description: 'Track and view the status of your return anytime.',
    };
  } else if (status === 'COMPLETED') {
    nbaPresentation = {
      svgIcon: 'bill',
      name: 'Your Equipment Return is Complete!',
      description: 'You\'re all set.',
    };
  } else {
    return null;
  }

  return {
    id: NBA_RETURN_STATUS,
    color: 'minty',
    link: RETURNS_ORDER_STATUS,
    linkText: 'View Details',
    dismissible: true,
    ...nbaPresentation,
  };
};

export const buildEligibleDeviceSwapNba = (swapData = {}) => {
  const { devices = [], bannerAlert } = swapData;
  const showNba = devices.length > 0 && bannerAlert;

  if (!showNba) return null;

  return {
    id: NBA_ELIGIBLE_DEVICE_SWAP,
    color: 'misc',
    svgIcon: 'devices',
    name: 'You\'re eligible for a free equipment update',
    description: 'Trade in your old equipment today. ',
    link: SWAPS_NEW,
    linkText: 'Get Started',
    dismissible: true,
  };
};

export const buildOutageNba = ({
  outageData = null,
  deviceStatus = null,
  accountData = {},
  itgData = null,
} = {}) => resolveOutageNba({
  outageData,
  deviceStatus,
  itg: itgData,
  subscribedLobs: getSubscribedLobs(accountData),
});
